import { ElementRef, forwardRef, useState } from 'react'

import Image from 'next/image'
import type { CSSProps, VariantProps } from 'style'

import { getInitials } from 'lib/utils/strings'

import { Fallback, Root } from './avatar.styled'

const NAME = 'Avatar'

type AvatarVariants = VariantProps<typeof Root>
type AvatarCustomProps = {
  src?: string | null
  alt?: string
  name?: string | null
}

type AvatarProps = AvatarCustomProps & AvatarVariants & CSSProps

// Should match avatar.styled root variants sizing
const getSize = (size: AvatarVariants['size']) => {
  switch (size) {
    case 'xs':
    case 'sm':
      return {
        width: 40,
        height: 40,
      }
    case 'lg':
      return {
        width: 64,
        height: 64,
      }
    case 'md':
    default:
      return {
        width: 48,
        height: 48,
      }
  }
}

const Avatar = forwardRef<ElementRef<typeof Root>, AvatarProps>(
  ({ src, name, css, size = 'md' }, ref) => {
    const initials = name ? getInitials(name) : '?'
    const [error, setError] = useState(false)

    return (
      <Root ref={ref} css={css} size={size}>
        {!!src && !error && (
          <Image
            src={src}
            alt={name ?? initials ?? ''}
            {...getSize(size)}
            loading="eager"
            onError={() => setError(true)}
          />
        )}
        {(!src || error) && <Fallback>{initials}</Fallback>}
      </Root>
    )
  },
)

Avatar.displayName = NAME

export default Avatar
